import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";

import {
  getCallConnection,
  invokeCallHub,
  registerHandler,
  startCallConnection,
} from "@/realtime/callHubClient.js";
import { CALL_STATES } from "@/calling/callConstants.js";
import { useCallEngine } from "@/calling/hooks/useCallEngine.js";
import { CALL_HUB_EVENTS, CALL_HUB_METHODS } from "@/calling/realtime/callEvents.js";
import {
  departmentDisplayName,
  CANONICAL_DEPARTMENTS,
  normalizeDepartment,
  isAllDepartment,
} from "@/utils/alerts/departmentAlerts.js";

const normalizeSessionId = (session = {}) =>
  session.id || session.Id || session.sessionId || session.SessionId || "";

const callIdFrom = (payload = {}) =>
  payload.callId || payload.CallId || payload.sourceId || payload.SourceId || "";

const emitIncomingCallLookupResult = (detail = {}) => {
  window.dispatchEvent(new CustomEvent("hotel:incomingCallLookupResult", { detail }));
};

const getRegisteredDepartments = () => {
  const savedDepartment = normalizeDepartment(localStorage.getItem("hotelDepartmentFilter") || "Reception");
  if (isAllDepartment(savedDepartment)) {
    return CANONICAL_DEPARTMENTS;
  }

  return [savedDepartment];
};

const mapIncomingHotelCall = (payload = {}) => {
  const callId = callIdFrom(payload);
  const callerName =
    payload.callerDisplayName ||
    payload.CallerDisplayName ||
    payload.callerName ||
    payload.CallerName ||
    payload.guestName ||
    payload.GuestName ||
    payload.staffName ||
    payload.StaffName ||
    "Staff Member";
  const roomName = payload.roomName || payload.RoomName;
  const targetDepartment = normalizeDepartment(payload.targetDepartment || payload.TargetDepartment || "Reception");
  const targetDepartmentLabel = departmentDisplayName(targetDepartment);
  const rawCallerDepartment = payload.callerDepartment || payload.CallerDepartment;
  const callerDepartment = rawCallerDepartment ? normalizeDepartment(rawCallerDepartment) : "";
  const callerDepartmentLabel = callerDepartment ? departmentDisplayName(callerDepartment) : "";

  return {
    ...payload,
    callId,
    title: `${callerName} is calling`,
    subtitle: roomName
      ? `Room ${roomName} -> ${targetDepartmentLabel}`
      : callerDepartment
        ? `${callerDepartmentLabel} -> ${targetDepartmentLabel}`
        : `Incoming ${targetDepartmentLabel} call`,
    targetDepartment,
    callerDepartment,
    roomName,
  };
};

const createStaffHubClient = ({
  connection,
  authContext,
  departments,
}) => {
  const staffName = authContext?.userFirstName || authContext?.email || "Staff";
  const registerCallDesk = () => invokeCallHub(connection, "RegisterCallDesk", {
    departments,
    staffName,
    receiveAllDepartments: departments.length >= CANONICAL_DEPARTMENTS.length,
  });

  return {
    connection,
    startConnection: () => startCallConnection({ mode: "staff" }),
    invoke: (methodName, payload = {}) => {
      if (
        methodName === CALL_HUB_METHODS.registerParticipant ||
        methodName === CALL_HUB_METHODS.registerReceiver
      ) {
        return registerCallDesk();
      }

      if (methodName === CALL_HUB_METHODS.startCall) {
        const target = payload.target || {};
        if (target.targetType === "guest-session") {
          return invokeCallHub(connection, "StartReceptionCall", {
            guestSessionId: target.targetId,
          });
        }

        if (target.targetType === "department") {
          const metadata = target.metadata || {};
          return invokeCallHub(connection, "StartDepartmentCall", {
            targetDepartmentId: metadata.targetDepartmentId || null,
            targetDepartment: metadata.targetDepartment || target.targetId,
            callerDepartment: metadata.callerDepartment,
            staffName,
          });
        }

        if (target.targetType === "staff-user") {
          const metadata = target.metadata || {};
          return invokeCallHub(connection, "StartStaffCall", {
            targetUserId: target.targetId,
            callerDepartment: metadata.callerDepartment,
            staffName,
          });
        }
      }

      return invokeCallHub(connection, methodName, payload);
    },
    registerHandler: (eventName, handler) => {
      const eventAliases = {
        [CALL_HUB_EVENTS.participantRegistered]: ["ReceptionRegistered", "CallDeskRegistered"],
        [CALL_HUB_EVENTS.receiverRegistered]: ["ReceptionRegistered", "CallDeskRegistered"],
        [CALL_HUB_EVENTS.incomingCall]: ["IncomingCall", "IncomingGuestCall"],
      }[eventName] || [eventName];

      const cleanups = eventAliases.map((alias) => registerHandler(connection, alias, handler));
      return () => cleanups.forEach((cleanup) => cleanup());
    },
  };
};

export const useReceptionCallDesk = ({ remoteAudioRef, authContext, onIncomingCallAlert }) => {
  const connection = useMemo(() => {
    try {
      return getCallConnection({ mode: "staff" });
    } catch {
      return null;
    }
  }, [authContext?.accessToken, authContext?.organizationId]);
  const [departmentVersion, setDepartmentVersion] = useState(0);
  const [callableGuestSessions, setCallableGuestSessions] = useState(new Set());
  const departments = useMemo(() => getRegisteredDepartments(), [departmentVersion]);
  const identity = useMemo(() => ({
    participantId: authContext?.userId || authContext?.email || "staff",
    participantType: "staff-user",
    displayName: authContext?.userFirstName || authContext?.email || "Staff",
    tenantId: authContext?.organizationId || "",
    receiveScopes: departments,
    metadata: {
      email: authContext?.email || "",
    },
  }), [authContext?.email, authContext?.organizationId, authContext?.userFirstName, authContext?.userId, departments]);
  const hubClient = useMemo(
    () => createStaffHubClient({ connection, authContext, departments }),
    [authContext, connection, departments],
  );

  const engine = useCallEngine({
    hubClient,
    identity,
    receiveScopes: departments,
    remoteAudioRef,
    mode: "receiver",
    mapIncomingCall: mapIncomingHotelCall,
    onIncomingCall: onIncomingCallAlert,
    onError: (message) => toast.error(message),
  });

  useEffect(() => {
    const authReady = Boolean(
      authContext?.authReady &&
        authContext?.accessToken &&
        authContext?.userId &&
        authContext?.organizationId
    );
    if (!authReady) return undefined;

    const updateSnapshot = (payload) => {
      const sessions = Array.isArray(payload)
        ? payload.map((id) => ({ guestSessionId: id, callable: true }))
        : payload?.sessions || payload?.Sessions || [];

      setCallableGuestSessions(new Set(
        sessions
          .filter((session) => session.callable ?? session.Callable ?? true)
          .map((session) => String(session.guestSessionId || session.GuestSessionId)),
      ));
    };

    const updatePresence = (payload = {}) => {
      const guestSessionId = payload.guestSessionId || payload.GuestSessionId || payload.sessionId || payload.SessionId;
      if (!guestSessionId) return;

      const callable = payload.callable ?? payload.Callable ?? payload.isOnline ?? payload.IsOnline ?? false;
      setCallableGuestSessions((current) => {
        const next = new Set(current);
        if (callable) next.add(String(guestSessionId));
        else next.delete(String(guestSessionId));
        return next;
      });

      window.dispatchEvent(new CustomEvent("hotel:guestPresenceChanged", {
        detail: payload,
      }));
    };

    const cleanup = [
      registerHandler(connection, "GuestPresenceSnapshot", updateSnapshot),
      registerHandler(connection, "GuestPresenceChanged", updatePresence),
    ];

    return () => cleanup.forEach((dispose) => dispose());
  }, [authContext?.accessToken, authContext?.authReady, authContext?.organizationId, authContext?.userId, connection]);

  useEffect(() => {
    const handleDepartmentChanged = () => setDepartmentVersion((version) => version + 1);
    window.addEventListener("hotel:departmentChanged", handleDepartmentChanged);
    return () => {
      window.removeEventListener("hotel:departmentChanged", handleDepartmentChanged);
    };
  }, []);

  const status =
    engine.registrationState === "registered"
      ? "connected"
      : engine.registrationState || "idle";
  const registered = engine.registrationState === "registered";

  const startReceptionCall = useCallback(async (session) => {
    const guestSessionId = normalizeSessionId(session);
    if (!guestSessionId) return;

    await engine.startCall({
      targetId: guestSessionId,
      targetType: "guest-session",
      targetLabel: "Calling guest",
      subtitle: session?.roomName || session?.RoomName
        ? `Room ${session.roomName || session.RoomName}`
        : session?.guestName || session?.GuestName || "Guest",
      metadata: {
        guestName: session?.guestName || session?.GuestName || "",
        roomName: session?.roomName || session?.RoomName || "",
      },
    });
  }, [engine]);

  const startStaffCall = useCallback(async (user) => {
    const targetUserId = user.userId || user.UserId;
    if (!targetUserId) return;

    await engine.startCall({
      targetId: targetUserId,
      targetType: "staff-user",
      targetLabel: user.displayName || user.DisplayName || "Staff Member",
      subtitle: `Waiting for answer...`,
      metadata: {
        callerDepartment: normalizeDepartment(localStorage.getItem("hotelDepartmentFilter") || "Staff", "Staff"),
      },
    });
  }, [engine]);

  const startDepartmentCall = useCallback(async (department) => {
    if (!department) return;

    const targetDepartmentId = typeof department === "object" ? department.departmentId : null;
    const rawDepartment = typeof department === "object" ? department.department : department;
    const rawDepartmentLabel = typeof department === "object" ? department.departmentLabel : null;
    const targetDepartment = normalizeDepartment(rawDepartment);
    const targetDepartmentLabel = rawDepartmentLabel || departmentDisplayName(targetDepartment);
    const callerDepartment = normalizeDepartment(localStorage.getItem("hotelDepartmentFilter") || "Staff", "Staff");
    const callerDepartmentLabel = departmentDisplayName(callerDepartment);

    await engine.startCall({
      targetId: targetDepartment,
      targetType: "department",
      targetLabel: `Calling ${targetDepartmentLabel}`,
      subtitle: `Waiting for ${targetDepartmentLabel} to answer...`,
      metadata: {
        targetDepartmentId,
        targetDepartment,
        targetDepartmentLabel,
        callerDepartment,
        callerDepartmentLabel,
      },
    });
  }, [engine]);

  const openIncomingCall = useCallback(async ({ callId, action = "open", payload = {} } = {}) => {
    const normalizedCallId =
      callId ||
      payload.callId ||
      payload.CallId ||
      payload.data?.callId ||
      payload.Data?.CallId;

    if (!normalizedCallId) return;

    emitIncomingCallLookupResult({
      callId: normalizedCallId,
      status: "opening",
      message: "Opening incoming call...",
    });

    const result = await engine.openIncomingCall(normalizedCallId);
    const statusText = String(result?.status || result?.Status || "missed").toLowerCase();
    const resultCall = result?.call || result?.Call || payload;
    const message = result?.message || result?.Message || "";

    emitIncomingCallLookupResult({
      callId: normalizedCallId,
      status: statusText,
      message,
      call: resultCall,
    });

    if (statusText !== "ringing") {
      if (message) toast.info(message);
      return;
    }

    if (action === "decline") {
      await engine.declineCall();
      emitIncomingCallLookupResult({
        callId: normalizedCallId,
        status: "declined",
        message: "Call declined.",
      });
    }
  }, [engine]);

  return {
    callState: engine.callState,
    callInfo: engine.callInfo,
    muted: engine.isMuted,
    isHeld: engine.isHeld,
    isRemoteHeld: engine.isRemoteHeld,
    isTransferring: engine.isTransferring,
    transferringDepartment: engine.transferringDepartment,
    callDuration: engine.callDuration,
    status,
    registered,
    callableGuestSessions,
    setCallInfo: () => {},
    setCallState: () => {},
    resetCall: engine.cleanup,
    loadIceServers: () => Promise.resolve(),
    acceptCall: engine.acceptCall,
    declineCall: engine.declineCall,
    endCall: engine.endCall,
    holdCall: engine.holdCall,
    transferCall: engine.transferCall,
    createOffer: () => Promise.resolve(),
    handleSignal: () => {},
    handleCallEnded: engine.cleanup,
    toggleMute: engine.toggleMute,
    startReceptionCall,
    startDepartmentCall,
    startStaffCall,
    openIncomingCall,
  };
};
